/**
 * Os catálogos fixos da área Sistema, copiados palavra por palavra do admin atual
 * (`portal/admin.html:8776-8809` e `9298-9307`), na MESMA ordem de chaves: a ordem das chaves é a
 * ordem em que o legado desenha as listas (`Object.keys(...)`), e mudá-la mudaria a tela.
 *
 * POR QUE AQUI: estes quatro mapas são da seção "PARÂMETROS DO SISTEMA" do legado. A ficha da cliente
 * tem uma cópia PARCIAL de dois deles (`ficha-cliente/abas/comum-a3/rotulos.ts`, só as chaves que ela
 * lê); quando o principal subir os catálogos para `src/comum/`, este arquivo passa a reexportar.
 * O `PRODUTOS_LEAD` (usado pelo Roteiro) já existe inteiro e vem de `ficha-cliente/comum-ficha`.
 */

/** Os 8 produtos na chave de parâmetro, em ordem alfabética do rótulo (`PARAM_PRODUTOS_LABEL`, `admin.html:8776`). */
export const PARAM_PRODUTOS_LABEL: Readonly<Record<string, string>> = Object.freeze({
  acompanhamento_anual: 'Acompanhamento Anual',
  analise_proporcao_corporal_3d: 'Análise de Proporção Corporal 3D',
  coloracao_pessoal: 'Coloração Pessoal',
  estrategia_imagem_pessoal_profissional: 'Estratégia de Imagem Pessoal e Profissional (EIP)',
  mala_de_viagem: 'Mala de Viagem',
  montagem_de_looks: 'Montagem de Looks',
  personal_shopper: 'Personal Shopper',
  projetos_sob_medida: 'Projetos Sob Medida',
});

/** As 16 abas da ficha na chave de parâmetro, em ordem alfabética do rótulo (`PARAM_ABAS_LABEL`, `admin.html:8787`). */
export const PARAM_ABAS_LABEL: Readonly<Record<string, string>> = Object.freeze({
  anotacoes_lu: 'Anotações da Lu',
  arquivos: 'Arquivos',
  checklist_atendimento: 'Checklist - Atendimento',
  checklist_milhoes: 'Checklist - Milhões',
  coloracao: 'Coloração - Ficha Atendimento',
  depoimento_cliente: 'Depoimento da Cliente',
  dossie: 'Dossiê da Cliente',
  email_atendimento: 'E-mail Atendimento',
  leitura_estrategica_ia: 'Leitura Estratégica (IA)',
  lista_desejos: 'Lista de Desejos',
  manual_foto: 'Manual Foto Modelo',
  mensagens: 'Mensagens',
  personalidade: 'Personalidade',
  questionario_avaliativo: 'Questionário Avaliativo',
  questionario: 'Questionário Geral',
  radar_estilo_raiz: 'Radar de Estilo Raiz',
});

/** As duas visibilidades de uma aba (`PARAM_VIS_LABEL`, `admin.html:8806`). */
export const PARAM_VIS_LABEL: Readonly<Record<string, string>> = Object.freeze({
  ambos: 'Ambos',
  administradora: 'Administradora',
});

/**
 * A referência de mercado que aparece em letra pequena sob cada produto em "Preço Padrão Produto"
 * (`SUGESTAO_PRECOS_MERCADO`, `admin.html:9298`). Não é preço cadastrado, é só orientação.
 */
export const SUGESTAO_PRECOS_MERCADO: Readonly<Record<string, string>> = Object.freeze({
  estrategia_imagem_pessoal_profissional: 'Ref. mercado: R$1.500-R$3.000 (consultoria completa iniciante)',
  analise_proporcao_corporal_3d: 'Sem categoria equivalente na tabela, aproximação: R$100-R$400/h (hora avulsa)',
  coloracao_pessoal: 'Ref. mercado: R$380-R$1.200 (varia por cidade e metodologia)',
  montagem_de_looks: 'Ref. mercado: R$100-R$400/h (hora avulsa, sobe com reputação e nicho)',
  mala_de_viagem: 'Sem categoria equivalente na tabela, aproximação: R$100-R$400/h (hora avulsa)',
  personal_shopper: 'Ref. mercado: R$100-R$400/h (hora avulsa, sobe com reputação e nicho)',
  acompanhamento_anual:
    'Sem categoria equivalente direta, aproximação: R$8.000-R$40.000 (pacote corporativo) ou R$20.000-R$50.000+ (faturamento mensal de profissional estabelecida)',
  projetos_sob_medida: 'Ref. mercado: R$100-R$400/h (hora avulsa, sobe com reputação e nicho)',
});

/** As opções de um `Selecao` a partir de um mapa, na ordem das chaves, como os `<option>` do legado. */
export function opcoesDe(mapa: Readonly<Record<string, string>>): { valor: string; rotulo: string }[] {
  return Object.entries(mapa).map(([valor, rotulo]) => ({ valor, rotulo }));
}

/** Uma sub-aba: o identificador do legado (`data-sub`) e o rótulo exato do botão. */
export interface SubAba {
  readonly id: string;
  readonly rotulo: string;
}

/** As 11 sub-abas, na ordem do legado (`admin.html:1917-1927`); a primeira abre por padrão. */
export const SUB_ABAS: readonly SubAba[] = Object.freeze([
  { id: 'param-sub-administradores', rotulo: 'Usuários' },
  { id: 'param-sub-visibilidade', rotulo: 'Visibilidade Aba' },
  { id: 'param-sub-vinculos', rotulo: 'Vínculo Produto Aba' },
  { id: 'param-sub-email', rotulo: 'Vincular Produto E-mail' },
  { id: 'param-sub-precos', rotulo: 'Preço Padrão Produto' },
  { id: 'param-sub-manual-foto', rotulo: 'Manual Foto' },
  { id: 'param-sub-roteiro-produtos', rotulo: 'Roteiro Cliente' },
  { id: 'param-sub-pessoas-indicacao', rotulo: 'Pessoas Indicação' },
  { id: 'param-sub-gift-cards', rotulo: 'Gift Cards' },
  { id: 'param-sub-cupons', rotulo: 'Cupom Desconto' },
  { id: 'param-sub-cliente-comunidade', rotulo: 'Cliente Comunidade' },
]);
